import { useEffect, useMemo, useState } from 'react'
import { api } from '../services/api'
import { ageFromBirthDate } from '../utils/ageFromBirthDate'

const OBJECTIVES = [
  { id: 'gain_muscle', label: 'Ganancia de masa muscular' },
  { id: 'lose_fat', label: 'Pérdida de grasa' },
  { id: 'recomp', label: 'Recomposición corporal' },
  { id: 'maintain', label: 'Mantenimiento' },
  { id: 'ideal_weight', label: 'Peso ideal' },
  { id: 'cardio', label: 'Resistencia cardiovascular' },
]

const TEXT_FIELDS: Array<[string, string]> = [
  ['phone', 'Teléfono'],
  ['city', 'Ciudad'],
  ['profession', 'Profesión'],
  ['objectiveDetail', 'Descripción del objetivo'],
  ['daysAvailable', 'Días disponibles para entrenar'],
  ['gymName', 'Lugar de entrenamiento'],
  ['trainMinutes', 'Tiempo disponible para entrenar'],
  ['trainingExperience', '¿Cuánto tiempo lleva entrenando?'],
  ['trainingPreference', 'Entrenamiento de los últimos meses'],
  ['trainingNotes', 'Preferencia para los entrenamientos'],
  ['injuries', 'Lesiones previas'],
  ['diseases', 'Enfermedades o medicamentos'],
  ['specialPathology', 'Patología o directrices médicas'],
  ['dizziness', 'Mareos al entrenar'],
  ['heartMeds', 'Medicamentos de presión o corazón'],
  ['otherLimits', 'Otras consideraciones'],
  ['sport', 'Actividad deportiva'],
  ['smokeAlcohol', 'Tabaco o alcohol'],
  ['sleepHours', 'Horas de sueño'],
  ['workSchedule', 'Horario laboral o estudios'],
  ['eatOut', 'Comidas fuera de casa'],
  ['unwantedFoods', 'Alimentos no deseados'],
  ['waterLiters', 'Litros de agua al día'],
  ['supplements', 'Suplementación'],
  ['allergies', 'Intolerancias o alergias'],
  ['wantedFoods', 'Alimentos que desea incluir'],
  ['socialMeals', 'Compromisos sociales'],
  ['bodyFatPct', '% de grasa'],
]

const EMPTY = {
  phone: '',
  birthDate: '',
  city: '',
  gender: '',
  profession: '',
  objective: '',
  objectiveDetail: '',
  heightCm: '',
  weightKg: '',
  bodyFatPct: '',
  daysAvailable: '',
  trainDays: '',
  gymName: '',
  trainMinutes: '',
  trainingExperience: '',
  trainingPreference: '',
  trainingNotes: '',
  injuries: '',
  diseases: '',
  specialPathology: '',
  dizziness: '',
  heartMeds: '',
  otherLimits: '',
  sport: '',
  smokeAlcohol: '',
  sleepHours: '',
  workSchedule: '',
  mealsNow: '',
  mealsWanted: '',
  eatOut: '',
  unwantedFoods: '',
  waterLiters: '',
  supplements: '',
  allergies: '',
  wantedFoods: '',
  socialMeals: '',
}

function asText(value: unknown) {
  if (Array.isArray(value)) return value.join(', ')
  if (value == null) return ''
  return String(value)
}

export default function IntakeFollowupPanel({ studentId }: { studentId: string }) {
  const [data, setData] = useState<any>(null)
  const [answers, setAnswers] = useState<any>(EMPTY)
  const [msg, setMsg] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!studentId) return
    setLoading(true)
    api.get(`/nutrition/intake/student/${studentId}`).then((r) => {
      const pre = r.data?.prefill || {}
      const stored = r.data?.answers || {}
      setData(r.data)
      setAnswers({
        ...EMPTY,
        ...stored,
        phone: asText(stored.phone || pre.phone),
        birthDate: asText(stored.birthDate || pre.birthDate).slice(0, 10),
        gender: asText(stored.gender || pre.gender),
        weightKg: asText(stored.weightKg ?? pre.weightKg),
        heightCm: asText(stored.heightCm ?? pre.heightCm),
        trainDays: asText(stored.trainDays),
        mealsWanted: asText(stored.mealsWanted || r.data?.mealsWanted),
        objective: asText(stored.objective || r.data?.objective),
      })
    }).catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [studentId])

  const imc = useMemo(() => {
    const w = Number(answers.weightKg)
    const h = Number(answers.heightCm)
    if (!w || !h) return ''
    return (w / ((h / 100) ** 2)).toFixed(1)
  }, [answers.weightKg, answers.heightCm])

  const age = useMemo(() => ageFromBirthDate(answers.birthDate), [answers.birthDate])

  const set = (key: string, value: string) => setAnswers((prev: any) => ({ ...prev, [key]: value }))

  const save = async () => {
    setMsg('')
    try {
      const r = await api.put(`/nutrition/intake/student/${studentId}`, {
        ...answers,
        mealsWanted: answers.mealsWanted === '' ? null : Number(answers.mealsWanted),
        weightKg: answers.weightKg === '' ? null : Number(answers.weightKg),
        heightCm: answers.heightCm === '' ? null : Number(answers.heightCm),
      })
      setData(r.data)
      setMsg('Guardado. No se recalcularon los macros ni el menú, y la persona no vuelve a ver el formulario de bienvenida.')
    } catch (err: any) {
      setMsg(err?.response?.data?.message || 'No se pudo guardar')
    }
  }

  if (loading) return <p className="text-sm text-gray-500">Cargando perfil…</p>
  if (!data) return <p className="text-sm text-gray-500">No se pudo abrir el perfil.</p>

  const field = (label: string, key: string, type = 'text') => (
    <label key={key} className="block mb-2 text-sm text-[#111827]">
      <span className="text-xs text-gray-500">{label}</span>
      <input
        type={type}
        className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1 bg-white"
        value={answers[key] ?? ''}
        onChange={(e) => set(key, e.target.value)}
      />
    </label>
  )

  return (
    <div className="overflow-y-auto flex-1 text-sm text-[#111827]">
      <p className="text-xs text-gray-500 mb-2">
        {data.completed || answers.objective
          ? 'Perfil guardado. Puedes corregirlo. '
          : 'Perfil en blanco para completarlo aquí. '}
        IMC {imc || '—'}. El +300 o −500 de calorías se aplica en Plan Nutricional.
      </p>
      <p className="text-sm mb-2"><strong>{data.prefill?.fullName || 'Sin nombre'}</strong> · {data.prefill?.email}</p>
      {field('Teléfono', 'phone', 'tel')}
      {field('Fecha de cumpleaños', 'birthDate', 'date')}
      <label className="block mb-2 text-sm text-[#111827]">
        <span className="text-xs text-gray-500">Edad</span>
        <input
          type="text"
          readOnly
          aria-label="Edad"
          className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1 bg-gray-50"
          value={age == null ? '' : String(age)}
        />
      </label>
      <p className="text-xs text-gray-500 mb-1">Género</p>
      <select className="mb-2 w-full border border-gray-200 rounded-lg px-2 py-1 bg-white" value={answers.gender || ''} onChange={(e) => set('gender', e.target.value)}>
        <option value="">Sin definir</option>
        <option value="MALE">Masculino</option>
        <option value="FEMALE">Femenino</option>
        <option value="OTHER">Prefiero no decirlo</option>
      </select>
      {field('Estatura (cm)', 'heightCm', 'number')}
      {field('Peso (kg)', 'weightKg', 'number')}
      <p className="text-xs text-gray-500 mb-1">Objetivo</p>
      <select className="mb-2 w-full border border-gray-200 rounded-lg px-2 py-1 bg-white" value={answers.objective || ''} onChange={(e) => set('objective', e.target.value)}>
        <option value="">Sin definir</option>
        {OBJECTIVES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
      </select>
      <p className="text-xs text-gray-500 mb-1">Comidas que quiere al día</p>
      <select className="mb-2 w-full border border-gray-200 rounded-lg px-2 py-1 bg-white" value={answers.mealsWanted || ''} onChange={(e) => set('mealsWanted', e.target.value)}>
        <option value="">Sin definir</option>
        {[2, 3, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      {field('Comidas que hace hoy', 'mealsNow')}
      {field('Días de entreno', 'trainDays')}
      {TEXT_FIELDS.filter(([key]) => key !== 'phone').map(([key, label]) => field(label, key, key === 'bodyFatPct' ? 'number' : 'text'))}
      <button type="button" onClick={save} className="mt-2 px-4 py-2 rounded-xl bg-[#64ba30] text-white font-semibold">Guardar perfil</button>
      {msg ? <p className="text-xs mt-2 text-gray-600">{msg}</p> : null}
    </div>
  )
}
